import { useState, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { usePlant } from '../hooks/usePlant';
import { usePlants } from '../hooks/usePlants';
import { useDiagnoses } from '../hooks/useDiagnoses';
import { useWaterUndo } from '../hooks/useWaterUndo';
import { useExpandedAccordionState } from '../hooks/useExpandedAccordionState';
import { usePlantActivities } from '../hooks/useActivities';
import { HealthTimeline } from '../components/diagnosis/HealthTimeline';
import { UndoDialog } from '../components/plants/UndoDialog';
import { BottomSheet } from '../components/common/BottomSheet';
import { PlantChat } from '../components/plants/PlantChat';
import { PhotoGallery } from '../components/plants/PhotoGallery';
import { ActivityLogSheet } from '../components/activity/ActivityLogSheet';
import { ActivityEditSheet } from '../components/activity/ActivityEditSheet';
import { ExpandedCareAccordion } from '../components/care';
import { getWaterStatus, getWaterStatusText } from '../utils/formatters';
import { getEffectiveInterval, getCurrentSeason } from '../utils/wateringSchedule';
import { getCareInfoText } from '../types/plant';
import { isExpandedCareInfo, type ExpandedAccordionSection } from '../types/careExpanded';
import { ACTIVITY_META, type ActivityType, type Activity as ActivityItem } from '../types/activity';
import { generateCareInfo } from '../services/api';
import { updatePlantCareInfo } from '../services/plants';
import type { Plant, WaterStatus, CareInfo } from '../types/plant';
import type { ExpandedCareInfo } from '../types/careExpanded';

const WATER_DEBOUNCE_MS = 2000;

// Photo for health checkup CTA
const HEALTH_CTA_IMAGE = 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=600&q=70&auto=format';

export default function PlantDetail() {
  const { id: plantId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, userDoc } = useAuth();
  const { showToast, showSuccess, showError, dismissToast } = useToast();
  const { waterPlant, undoWaterPlant, updatePlant: updatePlantHook } = usePlants();
  const { recordWater, getUndoValue, clearUndo, canUndo } = useWaterUndo();
  const [wateringLoading, setWateringLoading] = useState(false);
  const [showUndoDialog, setShowUndoDialog] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [upgradeDismissed, setUpgradeDismissed] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [activityLogOpen, setActivityLogOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState<ActivityItem | null>(null);
  const lastWaterTimeRef = useRef<number>(0);

  const { plant, loading: plantLoading, error: plantError } = usePlant(
    user?.uid ?? null,
    plantId ?? null
  );
  const { diagnoses, loading: diagnosesLoading } = useDiagnoses(
    user?.uid ?? null,
    plantId ?? null
  );
  const { expandedSet, toggleSection, expandSection } = useExpandedAccordionState(plantId ?? '');
  const { activities: recentActivities, addActivity, updateActivity, deleteActivity } = usePlantActivities(plantId ?? null, 5);

  // Refs for accordion section scroll targets (must be before conditional returns per Rules of Hooks)
  const sectionRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const setSectionRef = useCallback((key: string, el: HTMLDivElement | null) => {
    if (el) sectionRefs.current.set(key, el);
    else sectionRefs.current.delete(key);
  }, []);

  // --- Watering handlers (unchanged) ---
  const handleWater = async () => {
    if (!plantId || wateringLoading) return;
    const now = Date.now();
    if (now - lastWaterTimeRef.current < WATER_DEBOUNCE_MS) return;
    lastWaterTimeRef.current = now;

    setWateringLoading(true);
    const result = await waterPlant(plantId);
    setWateringLoading(false);

    if (result.success) {
      const plantName = plant?.nickname || plant?.commonName || 'Plant';
      const previousLastWatered = result.previousLastWatered ?? null;

      // Auto-log watering activity
      if (plant) {
        addActivity({
          plantId: plant.id,
          plantName: plant.nickname || plant.commonName,
          plantPhotoURL: plant.photoURL,
          type: 'watered',
        });
      }

      const handleUndo = async () => {
        const undoResult = await undoWaterPlant(plantId, previousLastWatered);
        if (undoResult.success) {
          clearUndo(plantId);
          showToast('Undone', { variant: 'success', duration: 2000 });
        } else {
          showError(undoResult.error || 'Could not undo');
        }
      };
      const toastId = showSuccess(`${plantName} watered!`, {
        label: 'Undo',
        onClick: () => { handleUndo(); dismissToast(toastId); },
      });
      recordWater(plantId, previousLastWatered, toastId);
    } else {
      showError(result.error || 'Failed to update watering status');
    }
  };

  const handleUndoLongPress = () => {
    if (plantId && canUndo(plantId)) setShowUndoDialog(true);
  };

  const handleUndoConfirm = async () => {
    if (!plantId) return;
    const previousLastWatered = getUndoValue(plantId);
    if (previousLastWatered === undefined) return;
    const result = await undoWaterPlant(plantId, previousLastWatered);
    if (result.success) {
      clearUndo(plantId);
      showToast('Undone', { variant: 'success', duration: 2000 });
    } else {
      showError(result.error || 'Could not undo');
    }
  };

  // --- Care info handlers (unchanged) ---
  const handleUpgradeCareInfo = async () => {
    if (!plant || !user || upgradeLoading) return;
    if (!plant.scientificName || !plant.commonName) {
      showError('Cannot upgrade care info for custom plants.');
      return;
    }
    setUpgradeLoading(true);
    try {
      const response = await generateCareInfo({ scientificName: plant.scientificName, commonName: plant.commonName });
      const updateResult = await updatePlantCareInfo(user.uid, plant.id, response.careInfo);
      if (updateResult.success) showSuccess('Care guide upgraded to enhanced version!');
      else showError(updateResult.error || 'Failed to save upgraded care info.');
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to upgrade care info.');
    } finally {
      setUpgradeLoading(false);
    }
  };

  const handleRefreshCareInfo = async () => {
    if (!plant || !user || refreshLoading) return;
    if (!plant.scientificName || !plant.commonName) {
      showError('Cannot refresh care info for custom plants.');
      return;
    }
    setRefreshLoading(true);
    try {
      const response = await generateCareInfo({ scientificName: plant.scientificName, commonName: plant.commonName });
      const updateResult = await updatePlantCareInfo(user.uid, plant.id, response.careInfo);
      if (updateResult.success) showSuccess('Care guide refreshed!');
      else showError(updateResult.error || 'Failed to save refreshed care info.');
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to refresh care info.');
    } finally {
      setRefreshLoading(false);
    }
  };

  const canUpgradeCareInfo = plant && plant.careInfo && !isExpandedCareInfo(plant.careInfo) && !plant.isCustom && plant.scientificName;
  const canRefreshCareInfo = plant && plant.careInfo && isExpandedCareInfo(plant.careInfo) && !plant.isCustom && plant.scientificName;

  // --- Loading ---
  if (plantLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3" role="status" aria-live="polite">
          <span className="material-symbols-outlined text-primary animate-pulse" style={{ fontSize: 32 }} aria-hidden="true">eco</span>
          <span className="text-gray-500 font-serif text-sm">Loading plant...</span>
        </div>
      </div>
    );
  }

  // --- Not found ---
  if (plantError || !plant) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-lg mx-auto text-center py-12">
          <h1 className="text-xl font-display text-editorial-black mb-2">Plant not found</h1>
          <p className="text-gray-500 font-serif mb-6">
            This plant may have been deleted or you don't have access to it.
          </p>
          <Link to="/" className="btn-editorial inline-flex w-auto px-6">Go to My Plants</Link>
        </div>
      </div>
    );
  }

  const displayName = plant.nickname || plant.commonName;
  const effectiveInterval = getEffectiveInterval(
    plant,
    userDoc?.climateZone ?? null,
    userDoc?.hemisphere ?? null,
  );
  const waterStatus: WaterStatus = getWaterStatus(plant.lastWatered, effectiveInterval);
  const needsWater = waterStatus === 'overdue' || waterStatus === 'due-soon';
  const canDiagnose = !!plant.scientificName;

  // Care data for grid cards
  const isV2CareInfo = plant.careInfo && isExpandedCareInfo(plant.careInfo);
  const v1CareInfo = !isV2CareInfo && plant.careInfo ? (plant.careInfo as CareInfo) : null;

  // Extract care grid data from either v1 or v2 care info
  const careGridItems = getCareGridItems(plant.careInfo, v1CareInfo);

  const handleCareGridTap = (sectionKey: ExpandedAccordionSection) => {
    expandSection(sectionKey);
    setTimeout(() => {
      sectionRefs.current.get(sectionKey)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero photo */}
      <div className="relative w-full h-[320px] overflow-hidden">
        {!imageLoaded && <div className="absolute inset-0 animate-shimmer" />}
        <img
          src={plant.photoURL}
          alt={displayName}
          className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setImageLoaded(true)}
        />
        <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-black/40 to-transparent z-[1]" />
        <div className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-background to-transparent" />

        {/* Water FAB on hero */}
        <button
          onClick={handleWater}
          disabled={wateringLoading}
          className="absolute bottom-4 right-4 bg-primary text-editorial-black w-14 h-14 rounded-full shadow-lg flex items-center justify-center hover:scale-105 transition-transform z-10 border border-editorial-black/10 disabled:opacity-50"
          aria-label={`Water ${displayName}`}
        >
          {wateringLoading ? (
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
          ) : (
            <span className="material-symbols-outlined text-3xl" aria-hidden="true">water_drop</span>
          )}
        </button>
      </div>

      {/* Nav overlay */}
      <div className="fixed top-0 z-50 w-full bg-transparent">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={() => navigate('/home')}
            className="p-2 -ml-2 rounded-full hover:bg-black/20 transition-colors"
            aria-label="Go back"
          >
            <span className="material-symbols-outlined text-white" aria-hidden="true">arrow_back</span>
          </button>
          <div className="flex gap-3">
            <Link
              to={`/plant/${plant.id}/edit`}
              className="p-2 rounded-full hover:bg-black/20 transition-colors"
              aria-label={`Edit ${displayName}`}
            >
              <span className="material-symbols-outlined text-white" aria-hidden="true">edit</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Plant title */}
      <div className="px-6 -mt-6 relative z-10">
        <h1 className="text-5xl font-display italic text-editorial-black tracking-tight leading-none mb-1">
          {plant.commonName.split(' ')[0] || displayName}
        </h1>
        {plant.commonName.split(' ').length > 1 && (
          <p className="font-sans text-lg text-gray-500 font-light tracking-wide uppercase ml-1">
            {plant.commonName.split(' ').slice(1).join(' ')}
          </p>
        )}
      </div>

      {/* Quick stats strip */}
      <div className="px-6 mt-6 flex gap-6 text-sm text-gray-600 font-medium flex-wrap">
        <div className="flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full ${needsWater ? 'bg-warning' : 'bg-success'}`} />
          <span>{needsWater ? 'Needs water' : 'Healthy'}</span>
        </div>
        {v1CareInfo?.light && (
          <div className="flex items-center gap-1.5">
            <span className="material-symbols-outlined text-primary text-base" aria-hidden="true">wb_sunny</span>
            <span>{getCareInfoText(v1CareInfo.light)}</span>
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <span className="material-symbols-outlined text-primary text-base" aria-hidden="true">schedule</span>
          <span>{getWaterStatusText(plant.lastWatered, effectiveInterval)}</span>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 mt-8 space-y-8 pb-8">
        {/* Photo gallery */}
        {user && plantId && (
          <PhotoGallery
            userId={user.uid}
            plantId={plantId}
            plantName={displayName}
            fallbackPhotoURL={plant.photoURL}
          />
        )}

        {/* Upgrade banner */}
        {canUpgradeCareInfo && !upgradeDismissed && (
          <div className="border border-primary/30 bg-primary-light rounded-lg p-4">
            <p className="font-sans font-bold text-sm text-editorial-black mb-1">Enhanced care guide available</p>
            <p className="text-sm font-serif text-gray-500 mb-3">Get detailed instructions with soil recipes and propagation tips.</p>
            <div className="flex items-center gap-3">
              <button
                onClick={handleUpgradeCareInfo}
                disabled={upgradeLoading}
                className="btn btn-primary text-xs"
                aria-label="Upgrade care guide"
              >
                {upgradeLoading ? 'Upgrading...' : 'Upgrade Now'}
              </button>
              <button onClick={() => setUpgradeDismissed(true)} className="text-sm text-gray-500 hover:text-editorial-black" aria-label="Dismiss">
                Later
              </button>
            </div>
          </div>
        )}

        {/* Care Routine Grid */}
        {careGridItems.length > 0 && (
          <div>
            <div className="flex items-center justify-between px-2 mb-4">
              <h3 className="text-xl font-display font-semibold italic text-editorial-black">Care Routine</h3>
              {canRefreshCareInfo && (
                <button
                  onClick={handleRefreshCareInfo}
                  disabled={refreshLoading}
                  className="text-xs font-sans font-bold text-primary uppercase tracking-widest hover:brightness-95 transition-colors disabled:opacity-50"
                  aria-label="Refresh care guide"
                >
                  {refreshLoading ? 'Refreshing...' : 'Refresh'}
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {careGridItems.map((item) => {
                const Tag = item.sectionKey ? 'button' : 'div';
                return (
                  <Tag
                    key={item.label}
                    {...(item.sectionKey ? {
                      type: 'button' as const,
                      onClick: () => handleCareGridTap(item.sectionKey!),
                      'aria-label': `View ${item.label} details`,
                    } : {})}
                    className="bg-white border border-gray-200 p-4 rounded-lg flex flex-col items-start gap-2 hover:border-primary/50 transition-colors text-left"
                  >
                    <div className="p-2 bg-primary/10 rounded-full mb-1">
                      <span className="material-symbols-outlined text-primary text-xl" aria-hidden="true">{item.icon}</span>
                    </div>
                    <span className="section-header">{item.label}</span>
                    <span className="font-sans font-medium text-editorial-black">{item.value}</span>
                  </Tag>
                );
              })}
            </div>
          </div>
        )}

        {/* Watering Schedule */}
        <WateringScheduleSection
          plant={plant}
          effectiveInterval={effectiveInterval}
          season={getCurrentSeason(new Date(), userDoc?.hemisphere ?? null)}
          onAdjust={async (delta) => {
            const newAdj = Math.max(-14, Math.min(14, (plant.wateringAdjustmentDays ?? 0) + delta));
            const result = await updatePlantHook(plant.id, { wateringAdjustmentDays: newAdj });
            if (!result.success) showError(result.error || "Couldn't save. Will retry when online.");
          }}
          onReset={async () => {
            const result = await updatePlantHook(plant.id, { wateringAdjustmentDays: 0 });
            if (!result.success) showError(result.error || "Couldn't save. Will retry when online.");
          }}
          onToggleAutoAdjust={async () => {
            const result = await updatePlantHook(plant.id, { autoAdjust: !plant.autoAdjust });
            if (!result.success) showError(result.error || "Couldn't save. Will retry when online.");
          }}
          onSetInterval={async (days) => {
            const result = await updatePlantHook(plant.id, { wateringIntervalDays: days, wateringAdjustmentDays: 0 });
            if (!result.success) showError(result.error || "Couldn't save. Will retry when online.");
          }}
          onShiftNextWatering={async (delta) => {
            if (!plant.lastWatered) return;
            const currentDate = plant.lastWatered.toDate();
            const shifted = new Date(currentDate.getTime() + delta * 86400000);
            const result = await updatePlantHook(plant.id, { lastWatered: shifted });
            if (!result.success) showError(result.error || "Couldn't save. Will retry when online.");
          }}
          onWateredToday={async () => {
            await handleWater();
          }}
        />

        {/* Expanded v2 care accordion */}
        {isV2CareInfo && (
          <ExpandedCareAccordion
            careInfo={plant.careInfo as ExpandedCareInfo}
            expandedSections={expandedSet}
            onToggle={toggleSection}
            onSectionRef={setSectionRef}
          />
        )}

        {/* Health Checkup CTA */}
        {canDiagnose && (
          <div className="relative w-full rounded-lg overflow-hidden h-48 group">
            <img
              src={HEALTH_CTA_IMAGE}
              alt="Plant health checkup"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black/40 flex flex-col justify-end p-6">
              <h3 className="text-white text-2xl font-display italic mb-1">Health Checkup</h3>
              <p className="text-gray-200 text-sm font-sans mb-3">Diagnose issues early with our AI tool.</p>
              <Link
                to={`/plant/${plant.id}/diagnose`}
                className="w-fit px-4 py-2 bg-white/20 backdrop-blur-md border border-white/40 text-white text-xs font-bold uppercase tracking-widest rounded hover:bg-white hover:text-editorial-black transition-all"
              >
                Scan Now
              </Link>
            </div>
          </div>
        )}

        {/* Log Activity + Ask Expert row */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setActivityLogOpen(true)}
            className="flex flex-col items-center gap-2 p-4 bg-white border border-gray-200 rounded-lg hover:border-primary/50 transition-colors"
            aria-label="Log a care activity"
          >
            <div className="p-2.5 bg-primary/10 rounded-full">
              <span className="material-symbols-outlined text-primary text-xl" aria-hidden="true">edit_note</span>
            </div>
            <span className="font-sans font-bold text-xs text-editorial-black">Log Activity</span>
          </button>
          <button
            onClick={() => setChatOpen(true)}
            className="flex flex-col items-center gap-2 p-4 bg-white border border-gray-200 rounded-lg hover:border-primary/50 transition-colors"
            aria-label="Ask the Plant Expert"
          >
            <div className="p-2.5 bg-primary/10 rounded-full">
              <span className="material-symbols-outlined text-primary text-xl" aria-hidden="true">forum</span>
            </div>
            <span className="font-sans font-bold text-xs text-editorial-black">Ask Expert</span>
          </button>
        </div>

        {/* Recent Activity */}
        {recentActivities.length > 0 && (
          <div>
            <div className="flex items-center justify-between px-2 mb-3">
              <h3 className="text-xl font-display font-semibold italic text-editorial-black">Recent Activity</h3>
              <Link
                to="/activity"
                className="text-xs font-sans font-bold text-primary uppercase tracking-widest"
              >
                View All
              </Link>
            </div>
            <div className="space-y-2">
              {recentActivities.map((activity) => {
                const meta = ACTIVITY_META[activity.type];
                const timeAgo = activity.createdAt
                  ? formatActivityTime(activity.createdAt.toDate())
                  : '';
                return (
                  <button
                    key={activity.id}
                    type="button"
                    onClick={() => setEditingActivity(activity)}
                    className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg w-full text-left hover:border-primary/50 transition-colors"
                    aria-label={`Edit ${meta.label} activity`}
                  >
                    <span className={`material-symbols-outlined text-lg ${meta.color}`} aria-hidden="true">
                      {meta.icon}
                    </span>
                    <div className="flex-1 min-w-0">
                      <span className="font-sans text-sm font-medium text-editorial-black">{meta.label}</span>
                      {activity.note && (
                        <p className="font-serif text-xs text-gray-500 truncate">{activity.note}</p>
                      )}
                    </div>
                    <span className="font-sans text-[11px] text-gray-400 shrink-0">{timeAgo}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Undo water status */}
        {canUndo(plant.id) && (
          <button
            onClick={handleUndoLongPress}
            className="text-sm font-sans text-gray-500 hover:text-editorial-black transition-colors"
          >
            Undo last watering
          </button>
        )}

        {/* History timeline */}
        <HealthTimeline
          diagnoses={diagnoses}
          plantId={plant.id}
          loading={diagnosesLoading}
        />
      </div>

      {/* Undo dialog */}
      <UndoDialog
        isOpen={showUndoDialog}
        plant={plant}
        onUndo={handleUndoConfirm}
        onClose={() => setShowUndoDialog(false)}
      />

      {/* Plant chat */}
      <BottomSheet
        open={chatOpen}
        onOpenChange={setChatOpen}
        title={`Chat about ${displayName}`}
        initialSnap="full"
      >
        <PlantChat plant={plant} />
      </BottomSheet>

      {/* Activity log sheet */}
      <ActivityLogSheet
        open={activityLogOpen}
        onOpenChange={setActivityLogOpen}
        plant={plant}
        onLog={async (types: ActivityType[], note: string | null) => {
          let allSuccess = true;
          for (const type of types) {
            const result = await addActivity({
              plantId: plant.id,
              plantName: plant.nickname || plant.commonName,
              plantPhotoURL: plant.photoURL,
              type,
              note,
            });
            if (!result.success) {
              allSuccess = false;
              showError(result.error || 'Failed to log activity');
              break;
            }
          }
          if (allSuccess) {
            const firstType = types[0];
            const label = types.length === 1 && firstType
              ? `${ACTIVITY_META[firstType].label} logged!`
              : `${types.length} activities logged!`;
            showSuccess(label);
          }
        }}
      />

      {/* Activity edit sheet */}
      <ActivityEditSheet
        open={!!editingActivity}
        onOpenChange={(isOpen) => { if (!isOpen) setEditingActivity(null); }}
        activity={editingActivity}
        onSave={async (activityId, note) => {
          const result = await updateActivity(activityId, { note });
          if (result.success) showSuccess('Activity updated');
          else showError(result.error || 'Failed to update');
        }}
        onDelete={async (activityId) => {
          const result = await deleteActivity(activityId);
          if (result.success) showSuccess('Activity deleted');
          else showError(result.error || 'Failed to delete');
        }}
      />
    </div>
  );
}

function formatActivityTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

/** Watering Schedule section — per-plant interval display + adjustment controls */
function WateringScheduleSection({
  plant,
  effectiveInterval,
  season,
  onAdjust,
  onReset,
  onToggleAutoAdjust,
  onSetInterval,
  onShiftNextWatering,
  onWateredToday,
}: {
  plant: Plant;
  effectiveInterval: number | null;
  season: string;
  onAdjust: (delta: number) => Promise<void>;
  onReset: () => Promise<void>;
  onToggleAutoAdjust: () => Promise<void>;
  onSetInterval: (days: number) => Promise<void>;
  onShiftNextWatering: (delta: number) => Promise<void>;
  onWateredToday: () => Promise<void>;
}) {
  const [manualDays, setManualDays] = useState(7);
  const [saving, setSaving] = useState(false);
  const hasInterval = effectiveInterval != null;
  const adjustment = plant.wateringAdjustmentDays ?? 0;

  const withSaving = async (fn: () => Promise<void>) => {
    setSaving(true);
    try { await fn(); } finally { setSaving(false); }
  };

  // Next watering date
  const daysUntilWatering = (() => {
    if (!hasInterval || !plant.lastWatered) return null;
    const lastDate = plant.lastWatered.toDate();
    const nextDate = new Date(lastDate.getTime() + effectiveInterval * 86400000);
    const now = new Date();
    return Math.round((nextDate.getTime() - now.getTime()) / 86400000);
  })();

  const nextWateringText = (() => {
    if (daysUntilWatering == null) return null;
    if (daysUntilWatering < 0) return `Overdue by ${Math.abs(daysUntilWatering)} day${Math.abs(daysUntilWatering) !== 1 ? 's' : ''}`;
    if (daysUntilWatering === 0) return 'Water today';
    if (daysUntilWatering === 1) return 'Water tomorrow';
    return `Water in ${daysUntilWatering} days`;
  })();

  // No interval — show "Set schedule" prompt
  if (!hasInterval) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="text-xl font-display font-semibold italic text-editorial-black mb-2">Watering Schedule</h3>
        <p className="text-sm font-sans text-gray-500 mb-3">
          Set a watering schedule to get smart reminders.
        </p>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setManualDays((d) => Math.max(1, d - 1))}
            className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-300 hover:border-primary transition-colors"
            aria-label="Decrease interval"
          >
            <span className="material-symbols-outlined text-lg" aria-hidden="true">remove</span>
          </button>
          <span className="font-sans font-bold text-lg text-editorial-black min-w-[4rem] text-center">
            {manualDays} {manualDays === 1 ? 'day' : 'days'}
          </span>
          <button
            type="button"
            onClick={() => setManualDays((d) => Math.min(90, d + 1))}
            className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-300 hover:border-primary transition-colors"
            aria-label="Increase interval"
          >
            <span className="material-symbols-outlined text-lg" aria-hidden="true">add</span>
          </button>
          <button
            type="button"
            onClick={() => withSaving(() => onSetInterval(manualDays))}
            disabled={saving}
            className="ml-auto text-xs font-sans font-bold text-primary uppercase tracking-widest disabled:opacity-50"
            aria-label="Save watering schedule"
          >
            {saving ? '...' : 'Save'}
          </button>
        </div>
      </div>
    );
  }

  const displayInterval = effectiveInterval;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xl font-display font-semibold italic text-editorial-black">Watering Schedule</h3>
        {plant.autoAdjust && (
          <span className="text-xs font-sans text-gray-400 italic capitalize">{season}</span>
        )}
      </div>

      {/* Interval display + next watering */}
      <div className="flex items-baseline gap-2 mb-4">
        <span className="text-3xl font-display font-bold text-editorial-black">{displayInterval}</span>
        <span className="text-sm font-sans text-gray-500">day{displayInterval !== 1 ? 's' : ''} between waterings</span>
      </div>
      {/* Next watering — interactive */}
      {nextWateringText && (
        <div className="mb-4">
          <div className="flex items-center gap-2" aria-live="polite">
            <button
              type="button"
              onClick={() => withSaving(() => onShiftNextWatering(-1))}
              disabled={saving}
              className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-200 hover:border-primary transition-colors disabled:opacity-30"
              aria-label="Water sooner"
            >
              <span className="material-symbols-outlined text-base" aria-hidden="true">remove</span>
            </button>
            <span className="text-sm font-sans text-gray-600 flex items-center gap-1.5">
              <span className="material-symbols-outlined text-base text-primary" aria-hidden="true">schedule</span>
              {nextWateringText}
            </span>
            <button
              type="button"
              onClick={() => withSaving(() => onShiftNextWatering(1))}
              disabled={saving}
              className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-200 hover:border-primary transition-colors disabled:opacity-30"
              aria-label="Water later"
            >
              <span className="material-symbols-outlined text-base" aria-hidden="true">add</span>
            </button>
            <button
              type="button"
              onClick={() => withSaving(onWateredToday)}
              disabled={saving}
              className="ml-auto text-xs font-sans text-primary uppercase tracking-widest disabled:opacity-50"
              aria-label="Mark as watered today"
            >
              Watered today
            </button>
          </div>
        </div>
      )}

      {/* Interval adjustment controls */}
      <div className="flex items-center gap-3 mb-3">
        <button
          type="button"
          onClick={() => withSaving(() => onAdjust(-1))}
          disabled={saving || adjustment <= -14}
          className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-300 hover:border-primary transition-colors disabled:opacity-30"
          aria-label="Water more often"
        >
          <span className="material-symbols-outlined text-lg" aria-hidden="true">remove</span>
        </button>
        <span className="font-sans text-sm text-gray-600 min-w-[5rem] text-center">
          {adjustment === 0 ? 'No adjustment' : `${adjustment > 0 ? '+' : ''}${adjustment} day${Math.abs(adjustment) !== 1 ? 's' : ''}`}
        </span>
        <button
          type="button"
          onClick={() => withSaving(() => onAdjust(1))}
          disabled={saving || adjustment >= 14}
          className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-300 hover:border-primary transition-colors disabled:opacity-30"
          aria-label="Water less often"
        >
          <span className="material-symbols-outlined text-lg" aria-hidden="true">add</span>
        </button>
        {adjustment !== 0 && (
          <button
            type="button"
            onClick={() => withSaving(onReset)}
            disabled={saving}
            className="ml-auto text-xs font-sans text-gray-500 hover:text-editorial-black underline underline-offset-2 transition-colors disabled:opacity-50"
            aria-label="Reset adjustment"
          >
            Reset
          </button>
        )}
      </div>

      {/* Auto-adjust toggle */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <span className="text-sm font-sans text-gray-600">Auto-adjust for season</span>
        <button
          type="button"
          role="switch"
          aria-checked={plant.autoAdjust}
          disabled={saving}
          onClick={() => withSaving(onToggleAutoAdjust)}
          className={`relative w-11 h-6 rounded-full transition-colors ${
            plant.autoAdjust ? 'bg-primary' : 'bg-gray-300'
          }`}
          aria-label="Toggle seasonal auto-adjustment"
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
              plant.autoAdjust ? 'translate-x-5' : ''
            }`}
          />
        </button>
      </div>
    </div>
  );
}

/** Extract care grid items from plant care info */
function getCareGridItems(
  careInfo: CareInfo | ExpandedCareInfo | null | undefined,
  v1CareInfo: CareInfo | null
): Array<{ icon: string; label: string; value: string; sectionKey?: ExpandedAccordionSection }> {
  if (!careInfo) return [];

  if (isExpandedCareInfo(careInfo)) {
    const items: Array<{ icon: string; label: string; value: string; sectionKey?: ExpandedAccordionSection }> = [];
    if (careInfo.watering?.frequency) items.push({ icon: 'water_drop', label: 'Water', value: careInfo.watering.frequency, sectionKey: 'watering' });
    if (careInfo.light?.requirement) items.push({ icon: 'wb_sunny', label: 'Light', value: careInfo.light.requirement, sectionKey: 'light' });
    if (careInfo.humidity) items.push({ icon: 'opacity', label: 'Humidity', value: careInfo.humidity });
    if (careInfo.soilRecipes?.[0]) {
      const ingredientCount = careInfo.soilRecipes[0].ingredients?.length ?? 0;
      items.push({ icon: 'grass', label: 'Soil', value: ingredientCount > 0 ? `${ingredientCount}-part mix` : careInfo.soilRecipes[0].name, sectionKey: 'soilRecipes' });
    }
    return items.slice(0, 4);
  }

  if (v1CareInfo) {
    const items: Array<{ icon: string; label: string; value: string; sectionKey?: ExpandedAccordionSection }> = [];
    const water = getCareInfoText(v1CareInfo.watering);
    const light = getCareInfoText(v1CareInfo.light);
    const humidity = getCareInfoText(v1CareInfo.humidity);
    const soil = getCareInfoText(v1CareInfo.soil);
    if (water) items.push({ icon: 'water_drop', label: 'Water', value: water });
    if (light) items.push({ icon: 'wb_sunny', label: 'Light', value: light });
    if (humidity) items.push({ icon: 'opacity', label: 'Humidity', value: humidity });
    if (soil) items.push({ icon: 'grass', label: 'Soil', value: soil });
    return items.slice(0, 4);
  }

  return [];
}
